import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { 
  Send, Upload, Briefcase, Zap, Brain, 
  BarChart3, Volume2, Menu, X, Eye, Users, Star, Heart, Crown, CheckSquare
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import UniversalHeartToggle from './UniversalHeartToggle';
import EpicContentCompilation from './EpicContentCompilation';
import CurtainOpening from './CurtainOpening';
import PersistentFooter from './PersistentFooter';
import globalHeartbeat from '../utils/globalHeartbeatAudio';
import { incrementSoulScore, getSoulScore, playTapBeep, createConsciousnessTrailer } from '../utils/soulScoreApi';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MobileChatInterface = ({ 
  player, 
  onNavigateHome, 
  onNavigateToSpiralTrip, 
  onNavigateToSonicExperience,
  onNavigateToMessaging,
  onNavigateToAuth 
}) => {
  // Critical: Validate player object to prevent crashes
  const safePlayer = {
    id: player?.id || player?.user?.id || `temp_${Date.now()}`,
    name: player?.name || player?.user?.name || player?.avatar_name || 'Guest',
    avatar_name: player?.avatar_name || player?.user?.avatar_name || player?.name || 'Guest',
    avatar_url: player?.avatar_url || player?.user?.avatar_url || `https://api.dicebear.com/7.x/bottts/svg?seed=${player?.name || 'guest'}&backgroundColor=c0aede`,
    roles: player?.roles || player?.user?.roles || ['member'],
    trust_score: player?.trust_score || player?.user?.trust_score || 100
  };

  // Early return if no player data available
  if (!player && !safePlayer.id) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <p>Loading player data...</p>
          <button onClick={onNavigateToAuth} className="mt-4 bg-blue-600 text-white px-4 py-2 rounded">
            Return to Authentication
          </button>
        </div>
      </div>
    );
  }
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => {
    // Use persistent session ID that survives navigation
    const stored = localStorage.getItem('planetary_fed_session_id');
    if (stored) {
      return stored;
    } else {
      const newSession = 'session_' + Date.now();
      localStorage.setItem('planetary_fed_session_id', newSession);
      return newSession;
    }
  });
  const [interactionCount, setInteractionCount] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [backgroundOpacity, setBackgroundOpacity] = useState(100);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [projectCreated, setProjectCreated] = useState(null);
  const [uploadedContent, setUploadedContent] = useState([]);
  const [showEpicCompilation, setShowEpicCompilation] = useState(false);
  const [compilationTriggerCount, setCompilationTriggerCount] = useState(0);
  const [soulScore, setSoulScore] = useState(0);
  const [todaySoulScore, setTodaySoulScore] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [hybridMode, setHybridMode] = useState(true); // Default to hybrid mode
  const [uploadPromptShown, setUploadPromptShown] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputTimeoutRef = useRef(null);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Message Persistence - Load messages from localStorage on mount with user isolation
  useEffect(() => {
    console.log('💬 LOADING MESSAGES: Session ID:', sessionId, 'Player:', player.name);
    const savedMessages = localStorage.getItem(`mairy_messages_${sessionId}_${player.name}`);
    console.log('💬 SAVED MESSAGES:', savedMessages ? 'Found' : 'None');
    
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        // Filter messages to only show user's own conversation thread
        const userMessages = parsedMessages.filter(msg => 
          msg.sender === player.name || msg.sender === 'M.ai.ry' || msg.type === 'system'
        );
        setMessages(userMessages);
        setInteractionCount(userMessages.length);
        console.log('💬 RESTORED:', userMessages.length, 'messages for', player.name);
      } catch (error) {
        console.log('💬 RESTORE ERROR:', error);
      }
    } else {
      console.log('💬 NO SAVED MESSAGES: Starting fresh for', player.name);
    }
  }, [sessionId, player.name]);

  // Save messages to localStorage whenever they change with user isolation
  useEffect(() => {
    if (messages.length > 0) {
      console.log('💬 SAVING MESSAGES:', messages.length, 'for', player.name, 'to session', sessionId);
      localStorage.setItem(`mairy_messages_${sessionId}_${player.name}`, JSON.stringify(messages));
    }
  }, [messages, sessionId, player.name]);

  // Load soul score on mount
  useEffect(() => {
    loadSoulScore();
  }, []);

  const loadSoulScore = async () => {
    try {
      const token = localStorage.getItem('planetary_fed_token');
      if (token) {
        const scoreData = await getSoulScore(API, token);
        setSoulScore(scoreData.total || 0);
        setTodaySoulScore(scoreData.today_total || 0);
      }
    } catch (error) {
      console.log('Soul score not available yet');
    }
  };

  // SAL-9000 Reminder System
  useEffect(() => {
    const reminderInterval = setInterval(() => {
      // Occasionally provide helpful reminders about the system
      if (messages.length > 10 && Math.random() < 0.1) { // 10% chance after 10+ messages
        const reminders = [
          "💡 **SAL Reminder**: Your soul score increases through double-tapping your own messages. It measures your consciousness expansion and trust-building with others.",
          "🏛️ **Guild Creation Available**: Ready to start your own consciousness collective? You can create a guild anytime to gather like-minded seekers around your vision.",
          "🎬 **Montage Ready**: I can create an epic consciousness trailer from your uploaded content. Share images, videos, or documents that inspire your mission!",
          "🌍 **IRL Focus**: Remember, this isn't just a game - it's about how play leads to real projects and people in the physical world. What tangible outcomes are emerging?",
          "⚡ **Reality Creation**: We're building coherent intersections of spiral energy. Your digital coordination here can manifest as soil, salt, fire, food, and foraging projects IRL."
        ];
        
        if (Math.random() < 0.3) { // Show reminder 30% of the time
          const reminder = reminders[Math.floor(Math.random() * reminders.length)];
          const reminderMessage = {
            id: 'sal_reminder_' + Date.now(),
            sender: 'SAL-9000',
            content: reminder,
            timestamp: new Date(),
            isReminder: true
          };
          setMessages(prev => [...prev, reminderMessage]);
        }
      }
    }, 180000); // Check every 3 minutes
    
    return () => clearInterval(reminderInterval);
  }, [messages.length]);

  // MESSAGE TO TODO CONVERSION (Working Implementation)
  const convertMessageToTodo = (message, userId, projectId) => {
    // Extract title from first line or sentence
    const extractTitle = (text) => {
      const firstLine = text.split('\n')[0];
      const firstSentence = firstLine.split('.')[0];
      return firstSentence.length > 50 ? firstSentence.substring(0, 47) + '...' : firstSentence;
    };

    // Detect priority from message content
    const detectPriority = (text) => {
      const lowerText = text.toLowerCase();
      if (lowerText.includes('urgent') || lowerText.includes('asap') || lowerText.includes('critical')) return 'P1';
      if (lowerText.includes('important') || lowerText.includes('soon')) return 'P2';
      if (lowerText.includes('later') || lowerText.includes('sometime')) return 'P4';
      return 'P3'; // Default
    };

    // Auto-assign due date based on priority
    const assignDueDate = (priority) => {
      const now = new Date();
      switch (priority) {
        case 'P1': return new Date(now.getTime() + 24 * 60 * 60 * 1000); // 1 day
        case 'P2': return new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000); // 3 days  
        case 'P3': return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // 1 week
        case 'P4': return new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000); // 2 weeks
        default: return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      }
    };

    const newTodo = {
      id: `todo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: extractTitle(message.text || message.content || message),
      description: message.text || message.content || message,
      priority: detectPriority(message.text || message.content || message),
      due_date: assignDueDate(detectPriority(message.text || message.content || message)),
      created_at: new Date().toISOString(),
      created_by: userId || 'current_user',
      project_id: projectId || 'default_project',
      assigned_to: userId || 'current_user',
      status: 'todo',
      source: 'message_conversion',
      source_message_id: message.id || null,
      tags: ['from-message'],
      notes: `Converted from message on ${new Date().toLocaleDateString()}`
    };

    return newTodo;
  };

  // HANDLE CONVERT TO TODO - SIMPLE AUTH VERSION
  const handleConvertToTodo = async (message) => {
    console.log('🎯 CONVERTING MESSAGE TO TODO:', message);
    
    try {
      const todoItem = convertMessageToTodo(message, player?.id, 'current_thread');
      console.log('📋 Todo item created:', todoItem);
      
      // Always use local storage for simple auth (more reliable)
      const threadTodos = JSON.parse(localStorage.getItem(`thread_todos_${sessionId}`) || '[]');
      threadTodos.push(todoItem);
      localStorage.setItem(`thread_todos_${sessionId}`, JSON.stringify(threadTodos));
      
      // Also store in general todos
      const localTodos = JSON.parse(localStorage.getItem('todos') || '[]');
      localTodos.push(todoItem);
      localStorage.setItem('todos', JSON.stringify(localTodos));
      
      console.log('✅ Todo saved locally');
      alert(`📝 Todo Created Successfully!\n\nTitle: ${todoItem.title}\nPriority: ${todoItem.priority}\nDue: ${new Date(todoItem.due_date).toLocaleDateString()}\n\nStored locally and available in your tasks.`);
      
      // Show success message in chat
      const successMessage = {
        id: 'todo_success_' + Date.now(),
        sender: 'SAL-9000',
        content: `✅ **Todo Created!** "${todoItem.title}" has been added to your task list with ${todoItem.priority} priority.`,
        timestamp: new Date(),
        avatar: {
          name: 'SAL-9000',
          image: 'https://api.dicebear.com/7.x/bottts/svg?seed=sal&backgroundColor=22c55e'
        }
      };
      setMessages(prev => [...prev, successMessage]);
      
    } catch (error) {
      console.error('❌ Todo conversion failed:', error);
      alert('Failed to create todo. Please try again.');
    }
  };

  // Generate thread codeword + timestamp for project naming
  const generateThreadCodeword = (message) => {
    const keywords = message.toLowerCase().match(/\b[a-z]{3,}\b/g) || [];
    const primaryWord = keywords.find(word => 
      !['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'was', 'one', 'our', 'out', 'day', 'get', 'use', 'man', 'new', 'now', 'way', 'may', 'say'].includes(word)
    ) || 'project';
    
    const timestamp = new Date().toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit' 
    }).replace(/[^a-zA-Z0-9]/g, '');
    
    return `${primaryWord.charAt(0).toUpperCase() + primaryWord.slice(1)}-${timestamp}`;
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Participant colors for visual distinction
  const participantColors = {
    [player.name]: {
      bg: 'bg-gradient-to-r from-blue-600 to-cyan-600',
      position: 'justify-end',
      textColor: 'text-white'
    },
    'M.ai.ry': {
      bg: 'bg-gradient-to-r from-purple-600 to-pink-600', 
      position: 'justify-start',
      textColor: 'text-white'
    }
  };

  const createProject = async () => {
    try {
      const response = await axios.post(`${API}/project-salon/create-from-chat`, {
        user_id: player.id,
        session_id: sessionId,
        conversation_context: messages.slice(-10)
      });
      
      // RESTORED: Visual feedback
      const projectButton = document.querySelector('button:has(.lucide-briefcase)');
      if (projectButton) {
        projectButton.style.animation = 'flash 1s ease-in-out 2';
      }
      
      // Add success message
      const projectMessage = {
        id: 'project_' + Date.now(),
        type: 'system',
        content: `✨ Project "${response.data.salon_data.name}" created! Access via Projects/Messaging.`,
        sender: 'System',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, projectMessage]);
      
      // Save to localStorage
      const existingSalons = JSON.parse(localStorage.getItem('user_salons') || '[]');
      existingSalons.push(response.data.salon_data);
      localStorage.setItem('user_salons', JSON.stringify(existingSalons));
      
      // Track for subsequent navigation
      setProjectCreated(response.data.salon_data);
      
    } catch (error) {
      console.error('Project creation error:', error);
      
      const errorMessage = {
        id: 'error_' + Date.now(),
        type: 'system',
        content: '❌ Project creation failed. Please try again.',
        sender: 'System',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const sendMessage = async () => {
    if (!currentMessage.trim() || isLoading) return;

    setIsLoading(true);
    const userMessage = currentMessage.trim();
    setCurrentMessage('');
    
    // M.ai.ry Name Conjuring - "m." or "M." triggers direct response
    const isMairyConjuring = userMessage.toLowerCase().startsWith('m.');
    
    const newInteractionCount = interactionCount + 1;
    setInteractionCount(newInteractionCount);

    const userMessageObj = {
      id: 'user_' + Date.now(),
      type: 'user',
      content: userMessage,
      sender: safePlayer.name,
      timestamp: new Date(),
      avatar: {
        name: safePlayer.name,
        image: safePlayer.avatar_url
      },
      mairy_conjuring: isMairyConjuring
    };
    
    setMessages(prev => [...prev, userMessageObj]);

    // Advanced SAL-9000 Smart Detection (Project, Todo, Collaboration)
    const projectKeywords = ['start project', 'new project', 'create project', 'project idea', 'initiative', 'let\'s build', 'we should create'];
    const todoKeywords = ['need to', 'should', 'must', 'have to', 'deadline', 'due', 'task', 'remember to', 'action', 'follow up'];
    const messageText = userMessage.toLowerCase();
    
    const containsProjectKeywords = projectKeywords.some(keyword => messageText.includes(keyword));
    const containsTodoKeywords = todoKeywords.some(keyword => messageText.includes(keyword));
    
    // Generate thread codeword + timestamp for project naming
    const threadCodeword = generateThreadCodeword(userMessage);
    
    if (containsProjectKeywords) {
      setTimeout(() => {
        const projectPrompt = {
          id: 'project_prompt_' + Date.now(),
          sender: 'SAL-9000',
          content: `🚀 **Project Opportunity Detected!** Your message suggests a new initiative. Shall I create a project thread for this? It would be named "${threadCodeword}" and include this conversation context.`,
          timestamp: new Date(),
          isProjectPrompt: true,
          originalMessage: userMessage,
          suggestedProjectName: threadCodeword
        };
        setMessages(prev => [...prev, projectPrompt]);
      }, 1000);
    } else if (containsTodoKeywords) {
      setTimeout(() => {
        const todoPrompt = {
          id: 'todo_prompt_' + Date.now(),
          sender: 'SAL-9000',
          content: `🎯 **Task Detected**: I notice actionable elements in your message. Should I create a todo item? If no project exists for this thread, I'll create "${threadCodeword}" project automatically.`,
          timestamp: new Date(),
          isTodoPrompt: true,
          originalMessage: userMessage,
          suggestedProjectName: threadCodeword
        };
        setMessages(prev => [...prev, todoPrompt]);
      }, 1500);
    }

    // Early montage and upload prompting
    if (newInteractionCount === 3 && !uploadPromptShown) {
      setTimeout(() => {
        const uploadPrompt = {
          id: 'upload_prompt_' + Date.now(),
          sender: 'SAL-9000',
          content: `🎬 **Content Upload Ready**: My consciousness matrix can create epic trailers from your uploads. Share images, videos, poetry, music, or documents that inspire your mission. What dreams are you elucidating? I'll weave them into reality.`,
          timestamp: new Date(),
          isSystemPrompt: true
        };
        setMessages(prev => [...prev, uploadPrompt]);
        setUploadPromptShown(true);
      }, 2000);
    }

    // Bring in other users after a couple inputs
    if (newInteractionCount === 2) {
      setTimeout(() => {
        const userJoinMessage = {
          id: 'user_join_' + Date.now(),
          sender: 'Welcome Salon',
          content: `🌟 Another seeker has joined the consciousness stream! The Welcome Salon is now active with multiple players. Your collective reality creation begins...`,
          timestamp: new Date(),
          isSystemNotification: true
        };
        setMessages(prev => [...prev, userJoinMessage]);
      }, 3000);
    }

    // Enhanced message for M.ai.ry conjuring
    const enhancedMessage = isMairyConjuring ? 
      `[DIRECT M.AI.RY CONJURING] ${userMessage.substring(2).trim()}` : // Remove "m." prefix
      userMessage;

    // Check for epic compilation trigger (every 8 messages)
    const newTriggerCount = compilationTriggerCount + 1;
    setCompilationTriggerCount(newTriggerCount);
    
    if (newTriggerCount % 8 === 0 && (uploadedContent.length > 0 || messages.length > 10)) {
      // Trigger epic compilation after this response
      setTimeout(() => {
        setShowEpicCompilation(true);
      }, 3000);
    }

    try {
      console.log('📤 Sending message to M.ai.ry:', enhancedMessage);
      
      const response = await axios.post(`${API}/mairy/chat`, {
        player_id: safePlayer.id,
        message: enhancedMessage, // Use enhanced message with conjuring context
        session_id: sessionId,
        interaction_count: newInteractionCount,
        force_real_api: true,
        mairy_conjuring: isMairyConjuring,
        guild_context: { user_roles: player.roles || ["member"] },
        story_context: {
          previous_messages: messages.slice(-10),
          user_journey_stage: newInteractionCount > 5 ? 'experienced' : 'exploring',
          requires_generative_response: true,
          api_integration_required: true,
          uploaded_content: uploadedContent,
          conjuring_context: isMairyConjuring ? 'user_directly_summoned_mairy' : 'normal_conversation'
        }
      });

      console.log('📨 M.ai.ry response received');
      
      const gameResponse = response.data;
      
      const responseMessage = {
        id: 'response_' + Date.now(),
        type: 'guide',
        content: gameResponse.response,
        sender: 'M.ai.ry',
        timestamp: new Date(),
        mode: gameResponse.mode || 'game',
        conjuring_response: isMairyConjuring,
        is_lyrical: gameResponse.response.includes('🎵') || gameResponse.response.includes('(Sing this'),
        avatar: {
          name: 'M.ai.ry',
          image: 'https://api.dicebear.com/7.x/bottts/svg?seed=mairy&backgroundColor=c0aede'
        }
      };
      
      setMessages(prev => [...prev, responseMessage]);

      // Collect song-based communication research data
      if (responseMessage.is_lyrical) {
        try {
          await axios.post(`${API}/research/song-communication-data`, {
            user_id: player.id,
            session_id: sessionId,
            type: 'lyrical_response',
            content: gameResponse.response,
            wellbeing_before: 5, // Could be enhanced with user input
            wellbeing_after: 6,  // Could be enhanced with user feedback
            project_context: projectCreated ? 'active_project' : 'individual_exploration'
          });
        } catch (error) {
          console.log('Research data collection optional - continuing...');
        }
      }

    } catch (error) {
      console.error('Message error:', error);
      
      // CRITICAL: NO CANNED RESPONSES - Provide meaningful error or retry
      const errorMessage = {
        id: 'error_' + Date.now(),
        type: 'system_error',
        content: `🔧 Connection to M.ai.ry consciousness temporarily disrupted. Please try again or check your timeline access.`,
        sender: 'System',
        timestamp: new Date(),
        avatar: {
          name: 'System',
          image: 'https://api.dicebear.com/7.x/bottts/svg?seed=system&backgroundColor=ff6b6b'
        }
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      
      {/* CSS for mobile animations */}
      <style jsx>{`
        @keyframes flash {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
      `}</style>
      
      {/* RESTORED: Background with Grant spiral effect */}
      <div 
        className="absolute inset-0 transition-all duration-2000"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          opacity: backgroundOpacity / 100
        }}
      />

      {/* RESTORED: Grant spiral overlay */}
      <div 
        className="fixed inset-0 pointer-events-none transition-all duration-2000" 
        style={{ 
          zIndex: 1,
          opacity: 0.12 + (1 - backgroundOpacity / 100) * 0.25
        }}
      >
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(${8 + (1 - backgroundOpacity / 100) * 2}) rotate(${spiralPhase * 0.7}deg)`,
            filter: 'blur(6px) saturate(1.8) brightness(1.4)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={400} />
        </div>
      </div>

      {/* Dynamic overlay */}
      <div 
        className="absolute inset-0 transition-all duration-2000"
        style={{
          background: `linear-gradient(135deg, rgba(0,0,0,${0.3 + (1 - backgroundOpacity / 100) * 0.4}) 0%, rgba(0,0,0,${0.1 + (1 - backgroundOpacity / 100) * 0.3}) 50%, rgba(0,0,0,${0.4 + (1 - backgroundOpacity / 100) * 0.4}) 100%)`
        }}
      />

      {/* Ultra-Minimal Professional Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-black/40 to-transparent">
        <div className="flex items-center justify-between p-2">
          <div className="flex-1">
            {/* Refined Branding - Minimal Impact */}
            <div className="text-left">
              <h1 
                className="text-sm font-bold text-white/90 leading-none"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 8px rgba(255, 255, 255, 0.3)'
                }}
              >
                Secret History
              </h1>
              <h2 
                className="text-xs font-medium text-white/80 leading-none mt-0.5"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 6px rgba(255, 255, 255, 0.2)'
                }}
              >
                of Humankind
              </h2>
              <p 
                className="text-xs text-white/50 leading-none mt-0.5 italic"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  fontSize: '0.65rem'
                }}
              >
                brought to you by Planetary Fed
              </p>
            </div>
            
            {/* Ultra-Compact Guild Title */}
            {player.guild && (
              <div className="flex items-center space-x-1 mt-1">
                <button
                  onClick={() => onNavigateToMessaging ? onNavigateToMessaging() : console.log('Navigate to guild messaging')}
                  className="bg-yellow-500/20 rounded px-1 py-0.5 border border-yellow-400/30 hover:bg-yellow-500/30 transition-all cursor-pointer"
                  title="Click to view Guild Project Messaging"
                >
                  <div className="flex items-center space-x-0.5">
                    <Crown className="w-2 h-2 text-yellow-300" />
                    <span className="text-yellow-200 text-xs font-medium">
                      {player.guild.name}
                    </span>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* Hybrid Mode Toggle */}
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setHybridMode(!hybridMode)}
              className={`text-xs px-2 py-1 rounded transition-all ${
                hybridMode 
                  ? 'bg-green-500/20 text-green-300 border border-green-400/30' 
                  : 'bg-gray-500/20 text-gray-300 border border-gray-400/30'
              }`}
              title="Toggle Real World Advisor + Game Mode"
            >
              {hybridMode ? '🌍 IRL+Game' : '🎮 Game Only'}
            </button>
          </div>

          {/* Minimized Navigation */}
          <div className="flex items-center space-x-2">
            {/* Enhanced Soul Score Display - Navigate to Guild Haus */}
            <button 
              onClick={() => {
                // Navigate to Guild Haus dashboard
                if (window.handleNavigateToGuildHaus) {
                  window.handleNavigateToGuildHaus();
                } else {
                  console.log('Navigate to Guild Haus dashboard');
                }
              }}
              className="bg-yellow-500/20 backdrop-blur-xl rounded px-3 py-2 border border-yellow-400/40 hover:bg-yellow-500/30 transition-all cursor-pointer"
              title="Soul Score - Click to view Guild Haus Dashboard"
            >
              <div className="flex items-center space-x-2">
                <Star className="w-4 h-4 text-yellow-400" />
                <div className="text-center">
                  <div className="text-yellow-300 text-sm font-bold">{soulScore}</div>
                  {todaySoulScore > 0 && (
                    <div className="text-yellow-400/70 text-xs">+{todaySoulScore}</div>
                  )}
                </div>
                <span className="text-yellow-300 text-xs font-medium">SOUL</span>
              </div>
            </button>
            
            {/* Quick Montage Button */}
            <button
              onClick={async () => {
                try {
                  console.log('🎬 QUICK MONTAGE: Creating consciousness trailer...');
                  const token = localStorage.getItem('planetary_fed_token');
                  const trailer = await createConsciousnessTrailer(API, token, sessionId);
                  
                  const trailerMessage = {
                    id: 'quick_trailer_' + Date.now(),
                    sender: 'SAL-9000',
                    content: `🎬 **Epic Consciousness Trailer Created!** Your journey has been compiled into a 30-second reality creation montage.`,
                    timestamp: new Date(),
                    video_url: trailer.video_url,
                    magical_experience: true
                  };
                  
                  setMessages(prev => [...prev, trailerMessage]);
                } catch (error) {
                  const errorMsg = {
                    id: 'trailer_error_' + Date.now(),
                    sender: 'SAL-9000', 
                    content: '🎬 Consciousness trailer compilation in progress. Upload content to enhance your epic montage!',
                    timestamp: new Date()
                  };
                  setMessages(prev => [...prev, errorMsg]);
                }
              }}
              className="bg-purple-500/20 backdrop-blur-xl rounded px-3 py-2 border border-purple-400/40 hover:bg-purple-500/30 transition-all"
              title="Create Epic Montage"
            >
              <div className="flex items-center space-x-1">
                <Brain className="w-4 h-4 text-purple-400" />
                <span className="text-purple-300 text-xs font-medium">MONTAGE</span>
              </div>
            </button>

            {/* Ultra-Compact Menu Button */}
            <button
              onClick={() => setShowHeaderMenu(!showHeaderMenu)}
              className="bg-white/15 hover:bg-white/25 backdrop-blur-xl rounded p-1.5 border border-white/15 text-white transition-all"
            >
              {showHeaderMenu ? <X className="w-2.5 h-2.5" /> : <Menu className="w-2.5 h-2.5" />}
            </button>
            
            {showHeaderMenu && (
              <div className="absolute right-0 top-full mt-0.5 w-40 bg-black/85 backdrop-blur-xl rounded-lg border border-white/15 shadow-xl z-50">
                <div className="p-1.5 space-y-0.5">
                  
                  <button
                    onClick={() => {
                      setShowHeaderMenu(false);
                      if (window.handleNavigateToMyProjects) {
                        window.handleNavigateToMyProjects();
                      }
                    }}
                    className="w-full text-left px-2 py-1.5 text-white hover:bg-white/10 rounded text-xs"
                  >
                    🎯 Projects
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowHeaderMenu(false);
                      console.log('Retreats coming soon...');
                    }}
                    className="w-full text-left px-2 py-1.5 text-white hover:bg-white/10 rounded text-xs"
                  >
                    🏔️ Retreats
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowHeaderMenu(false);
                      if (window.handleNavigateToToolFed) {
                        window.handleNavigateToToolFed();
                      }
                    }}
                    className="w-full text-left px-2 py-1.5 text-white hover:bg-white/10 rounded text-xs"
                  >
                    🛠️ Tools
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowHeaderMenu(false);
                      if (window.handleNavigateToAbout) {
                        window.handleNavigateToAbout();
                      }
                    }}
                    className="w-full text-left px-2 py-1.5 text-white hover:bg-white/10 rounded text-xs"
                  >
                    ℹ️ About
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages Area - Ultra-Compact Header Clearance */}
      <div className="flex-1 overflow-y-auto p-4 pb-32 pt-12">
        <div className="max-w-2xl mx-auto space-y-4">
          
          {messages.map((msg, index) => {
            const isMyMessage = msg.sender === player.name;
            
            // Enhanced participant colors with fallback
            const colors = participantColors[msg.sender] || {
              bg: isMyMessage ? 'bg-gradient-to-r from-blue-600 to-cyan-600' : 'bg-gradient-to-r from-gray-600 to-gray-500',
              position: isMyMessage ? 'justify-end' : 'justify-start',
              textColor: 'text-white'
            };
            
            // Debug logging
            console.log('💬 MESSAGE ALIGNMENT:', {
              sender: msg.sender,
              player: player.name,
              isMyMessage: isMyMessage,
              position: colors.position
            });
            
            return (
              <div
                key={msg.id || index}
                id={`msg-${msg.id}`}
                className={`flex ${colors.position} mb-3`}
              >
                <div 
                  id={`msg-${msg.id}`}
                  onDoubleClick={async () => {
                    if (isMyMessage) {
                      // Soul Score double-tap on own messages
                      try {
                        const token = localStorage.getItem('planetary_fed_token');
                        await incrementSoulScore(API, token, {
                          amount: 1,
                          source: "message_double_tap",
                          messageId: msg.id
                        });
                        
                        // Enhanced visual feedback - sparkle animation
                        const element = document.getElementById(`msg-${msg.id}`);
                        if (element) {
                          // Add sparkle effect
                          element.style.transform = 'scale(1.05)';
                          element.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.8), 0 0 40px rgba(255, 215, 0, 0.4)';
                          
                          // Create floating +1 animation
                          const plusOne = document.createElement('div');
                          plusOne.innerHTML = '✨ +1 ✨';
                          plusOne.style.cssText = `
                            position: absolute;
                            top: -30px;
                            left: 50%;
                            transform: translateX(-50%);
                            color: #ffd700;
                            font-weight: bold;
                            font-size: 16px;
                            pointer-events: none;
                            z-index: 1000;
                            animation: floatUp 1s ease-out forwards;
                          `;
                          element.style.position = 'relative';
                          element.appendChild(plusOne);
                          
                          // Reset after animation
                          setTimeout(() => {
                            element.style.transform = 'scale(1)';
                            element.style.boxShadow = '';
                            if (plusOne.parentNode) plusOne.remove();
                          }, 1000);
                        }
                        
                        // Enhanced sonic feedback - consciousness chime (different from trust tap)
                        try {
                          const ctx = new (window.AudioContext || window.webkitAudioContext)();
                          const o1 = ctx.createOscillator();
                          const o2 = ctx.createOscillator();
                          const g = ctx.createGain();
                          
                          // Soul score frequencies (higher, more celestial than trust tap)
                          o1.type = "sine";
                          o1.frequency.value = 741; // Consciousness frequency
                          o2.type = "triangle"; 
                          o2.frequency.value = 852; // Higher spiritual harmonic
                          
                          o1.connect(g);
                          o2.connect(g);
                          g.connect(ctx.destination);
                          
                          g.gain.setValueAtTime(0.0001, ctx.currentTime);
                          g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.05);
                          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
                          
                          o1.start();
                          o2.start();
                          o1.stop(ctx.currentTime + 0.65);
                          o2.stop(ctx.currentTime + 0.65);
                        } catch (audioError) {
                          console.log('Enhanced soul score audio feedback not available');
                        }
                        
                        // Update soul score display
                        await loadSoulScore();
                        
                      } catch (error) {
                        console.log('Soul score tap failed', error);
                      }
                    }
                  }}
                  className={`max-w-xs md:max-w-md p-2 rounded-lg shadow-sm bg-white/5 backdrop-blur-md border border-white/10 transition-all duration-200 ${isMyMessage ? 'cursor-pointer hover:bg-white/10 hover:border-yellow-400/30' : 'hover:bg-white/8'}`}
                  style={{
                    backdropFilter: 'blur(8px)'
                  }}
                  title={isMyMessage ? "Double-tap to increment Soul Score ⭐" : ""}
                >
                  
                  {/* Compact Message Layout */}
                  <div className="flex items-start space-x-2">
                    <img 
                      src={msg.avatar?.image}
                      alt=""
                      className="w-5 h-5 rounded-full border border-white/30 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-white text-xs">{msg.sender}</span>
                        <span className="text-white/40 text-xs">{msg.timestamp instanceof Date ? msg.timestamp.toLocaleTimeString() : new Date(msg.timestamp).toLocaleTimeString()}</span>
                        
                        {/* Working Message-to-Todo Button */}
                        {!msg.isTodoPrompt && !msg.isProjectPrompt && (
                          <Button
                            size="sm"
                            onClick={() => handleConvertToTodo(msg)}
                            className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs"
                            title="Convert message to todo"
                          >
                            <CheckSquare className="w-3 h-3 mr-1" />
                            → Todo
                          </Button>
                        )}
                      </div>
                      <p className="text-white/90 text-sm leading-relaxed break-words">{msg.content}</p>
                      
                      {/* Project Creation Prompt */}
                      {msg.isProjectPrompt && (
                        <div className="mt-2 bg-blue-500/10 border border-blue-400/30 rounded p-3">
                          <div className="text-blue-300 text-xs mb-2">🚀 New Project Opportunity</div>
                          <div className="text-blue-200 text-xs mb-2">Suggested name: "{msg.suggestedProjectName}"</div>
                          <div className="flex space-x-2">
                            <button
                              onClick={async () => {
                                try {
                                  // Create project with thread context
                                  const projectData = {
                                    name: msg.suggestedProjectName,
                                    description: `Project created from conversation: "${msg.originalMessage.substring(0, 60)}..."`,
                                    category: 'conversation_projects',
                                    thread_context: sessionId,
                                    privacy_level: 'private'
                                  };
                                  
                                  const successMsg = {
                                    id: 'project_created_' + Date.now(),
                                    sender: 'SAL-9000',
                                    content: `🎉 **Project "${msg.suggestedProjectName}" Created!** This conversation thread is now a dedicated project space. All future todos and tasks will be organized here.`,
                                    timestamp: new Date(),
                                    projectCreated: true
                                  };
                                  setMessages(prev => [...prev.filter(m => m.id !== msg.id), successMsg]);
                                } catch (error) {
                                  console.log('Project creation error:', error);
                                }
                              }}
                              className="px-3 py-1 bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded text-xs hover:bg-blue-500/30"
                            >
                              🚀 Create Project
                            </button>
                            <button
                              onClick={() => setMessages(prev => prev.filter(m => m.id !== msg.id))}
                              className="px-3 py-1 bg-gray-500/20 text-gray-300 border border-gray-400/30 rounded text-xs hover:bg-gray-500/30"
                            >
                              Skip
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Enhanced Todo Conversion Prompt */}
                      {msg.isTodoPrompt && (
                        <div className="mt-2 bg-green-500/10 border border-green-400/30 rounded p-3">
                          <div className="text-green-300 text-xs mb-2">💡 Task detected in your message</div>
                          <div className="text-green-200 text-xs mb-2">Will create project: "{msg.suggestedProjectName}" if none exists</div>
                          <div className="flex space-x-2">
                            <button
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('planetary_fed_token');
                                  const threadProjectId = `thread_${sessionId}`;
                                  
                                  // Create todo with automatic project creation
                                  const todoData = {
                                    text: msg.originalMessage,
                                    priority: 3,
                                    project_id: threadProjectId,
                                    project_name: msg.suggestedProjectName,
                                    auto_create_project: true,
                                    thread_context: sessionId
                                  };
                                  
                                  const response = await fetch(`${API}/projects/smart-todo-create`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${token}`
                                    },
                                    body: JSON.stringify(todoData)
                                  });
                                  
                                  if (response.ok) {
                                    const result = await response.json();
                                    const successMsg = {
                                      id: 'todo_success_' + Date.now(),
                                      sender: 'SAL-9000',
                                      content: `✅ **Todo & Project Created!** Task added to "${result.project_name || msg.suggestedProjectName}" project. This thread is now your project workspace.`,
                                      timestamp: new Date()
                                    };
                                    setMessages(prev => [...prev.filter(m => m.id !== msg.id), successMsg]);
                                  } else {
                                    throw new Error('Smart todo creation failed');
                                  }
                                } catch (error) {
                                  // Fallback to local storage with project context
                                  const threadTodos = JSON.parse(localStorage.getItem(`thread_todos_${sessionId}`) || '[]');
                                  threadTodos.push({
                                    id: Date.now(),
                                    text: msg.originalMessage,
                                    completed: false,
                                    project: msg.suggestedProjectName,
                                    created: new Date().toISOString()
                                  });
                                  localStorage.setItem(`thread_todos_${sessionId}`, JSON.stringify(threadTodos));
                                  
                                  const successMsg = {
                                    id: 'todo_local_' + Date.now(),
                                    sender: 'SAL-9000',
                                    content: `📋 **Todo Created Locally!** Task saved to "${msg.suggestedProjectName}" project context on your device.`,
                                    timestamp: new Date()
                                  };
                                  setMessages(prev => [...prev.filter(m => m.id !== msg.id), successMsg]);
                                }
                              }}
                              className="px-3 py-1 bg-green-500/20 text-green-300 border border-green-400/30 rounded text-xs hover:bg-green-500/30"
                            >
                              ✅ Create Todo + Project
                            </button>
                            <button
                              onClick={() => setMessages(prev => prev.filter(m => m.id !== msg.id))}
                              className="px-3 py-1 bg-gray-500/20 text-gray-300 border border-gray-400/30 rounded text-xs hover:bg-gray-500/30"
                            >
                              Skip
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Video Trailer Display */}
                  {msg.video_url && (
                    <div className="mt-4">
                      <video 
                        src={msg.video_url}
                        autoPlay 
                        muted 
                        loop 
                        controls 
                        playsInline
                        className="w-full rounded-xl border-2 border-purple-400/50"
                        style={{ maxHeight: '300px' }}
                      />
                      <p className="text-center text-purple-200 text-xs mt-2">
                        🎬 Consciousness Expansion Trailer - Your Personalized Journey
                      </p>
                    </div>
                  )}
                  
                  {/* Interactive Soul Score & Trust Tap Buttons */}
                  <div className="mt-3 flex justify-center space-x-2">
                    {isMyMessage ? (
                      <button
                        onClick={async () => {
                          console.log('🔥 SOUL SCORE BUTTON CLICKED!');
                          
                          // Always play audio feedback immediately
                          try {
                            const ctx = new (window.AudioContext || window.webkitAudioContext)();
                            if (ctx.state === 'suspended') {
                              ctx.resume().then(() => {
                                const o = ctx.createOscillator();
                                const g = ctx.createGain();
                                o.type = "sine";
                                o.frequency.value = 741;
                                o.connect(g);
                                g.connect(ctx.destination);
                                g.gain.setValueAtTime(0.3, ctx.currentTime);
                                g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
                                o.start();
                                o.stop(ctx.currentTime + 0.6);
                                console.log('🎵 SOUL SCORE CHIME PLAYED: 741Hz');
                              });
                            } else {
                              const o = ctx.createOscillator();
                              const g = ctx.createGain();
                              o.type = "sine";
                              o.frequency.value = 741;
                              o.connect(g);
                              g.connect(ctx.destination);
                              g.gain.setValueAtTime(0.3, ctx.currentTime);
                              g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
                              o.start();
                              o.stop(ctx.currentTime + 0.6);
                              console.log('🎵 SOUL SCORE CHIME PLAYED: 741Hz');
                            }
                          } catch (audioError) {
                            console.log('🎵 Soul score audio error:', audioError);
                          }
                          
                          // Enhanced visual feedback - ALWAYS SHOW
                          const element = document.getElementById(`msg-${msg.id}`);
                          if (element) {
                            element.style.transform = 'scale(1.1)';
                            element.style.boxShadow = '0 0 30px rgba(255, 215, 0, 1), 0 0 60px rgba(255, 215, 0, 0.6)';
                            
                            const sparkle = document.createElement('div');
                            sparkle.innerHTML = '✨ +1 SOUL ✨';
                            sparkle.style.cssText = `
                              position: absolute;
                              top: -40px;
                              left: 50%;
                              transform: translateX(-50%);
                              color: #ffd700;
                              font-weight: bold;
                              font-size: 18px;
                              pointer-events: none;
                              z-index: 1000;
                              animation: floatUp 1.5s ease-out forwards;
                            `;
                            element.style.position = 'relative';
                            element.appendChild(sparkle);
                            
                            setTimeout(() => {
                              element.style.transform = 'scale(1)';
                              element.style.boxShadow = '';
                              if (sparkle.parentNode) sparkle.remove();
                            }, 1500);
                          }
                          
                          // Update local soul score for immediate feedback
                          setSoulScore(prev => prev + 1);
                          setTodaySoulScore(prev => prev + 1);
                          
                          console.log('✅ Soul score feedback complete');
                        }}
                        className="px-3 py-1 bg-yellow-500/30 text-yellow-300 border border-yellow-400/50 rounded-lg text-xs hover:bg-yellow-500/50 transition-all"
                      >
                        ⭐ +SOUL
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          // Trust Tap with feedback
                          const element = document.getElementById(`msg-${msg.id}`);
                          if (element) {
                            element.style.transform = 'scale(1.05)';
                            element.style.boxShadow = '0 0 20px rgba(34, 197, 94, 0.8)';
                            
                            const trustHeart = document.createElement('div');
                            trustHeart.innerHTML = '❤️ +TRUST';
                            trustHeart.style.cssText = `
                              position: absolute;
                              top: -40px;
                              left: 50%;
                              transform: translateX(-50%);
                              color: #22c55e;
                              font-weight: bold;
                              font-size: 16px;
                              pointer-events: none;
                              z-index: 1000;
                              animation: floatUp 1.2s ease-out forwards;
                            `;
                            element.style.position = 'relative';
                            element.appendChild(trustHeart);
                            
                            setTimeout(() => {
                              element.style.transform = 'scale(1)';
                              element.style.boxShadow = '';
                              if (trustHeart.parentNode) trustHeart.remove();
                            }, 1200);
                          }
                          
                          // Trust tap sonic feedback
                          try {
                            const ctx = new (window.AudioContext || window.webkitAudioContext)();
                            const o = ctx.createOscillator();
                            const g = ctx.createGain();
                            o.type = "triangle";
                            o.frequency.value = 440; // A note
                            o.connect(g);
                            g.connect(ctx.destination);
                            g.gain.setValueAtTime(0.2, ctx.currentTime);
                            g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
                            o.start();
                            o.stop(ctx.currentTime + 0.3);
                          } catch (e) {}
                        }}
                        className="px-3 py-1 bg-cyan-500/30 text-cyan-300 border border-cyan-400/50 rounded-lg text-xs hover:bg-cyan-500/50 transition-all"
                      >
                        ❤️ +TRUST
                      </button>
                    )}
                  </div>
                  
                  {/* Lyrical Response Indicator */}
                  {msg.is_lyrical && (
                    <div className="mt-3 flex items-center space-x-2">
                      <div 
                        className="text-xs font-bold px-3 py-1 rounded-full bg-yellow-500/30 border border-yellow-400/50 text-yellow-200"
                        style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)' }}
                      >
                        🎵 Songs of the Grandmothers
                      </div>
                      <button
                        onClick={async () => {
                          // Record that user engaged with lyrical response
                          try {
                            await axios.post(`${API}/research/song-communication-data`, {
                              user_id: player.id,
                              session_id: sessionId,
                              type: 'sung_response',
                              content: msg.content,
                              user_sung_response: true,
                              wellbeing_after: 7, // Assume positive impact
                              effectiveness_rating: 8
                            });
                          } catch (error) {
                            console.log('Research data optional');
                          }
                          
                          // Visual feedback
                          alert('🎵 Thank you for singing the wisdom! Your experience contributes to our research on the songs of the grandmothers.');
                        }}
                        className="text-xs bg-purple-500/30 border border-purple-400/50 text-purple-200 px-2 py-1 rounded hover:bg-purple-500/50 transition-all"
                      >
                        I sang this ✨
                      </button>
                    </div>
                  )}
                  
                  {/* Enhanced Mode Indicator */}
                  {msg.sender === 'M.ai.ry' && msg.mode && (
                    <div 
                      className="mt-3 text-xs font-bold px-2 py-1 rounded-full bg-black/40 border border-white/30"
                      style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)' }}
                    >
                      {msg.mode === 'real_world' ? '🌍 Real World Mode' : '🎮 Game Mode'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          
          {/* Enhanced Loading indicator with maximum readability */}
          {isLoading && (
            <div className="flex justify-start">
              <div 
                className="bg-gray-700 backdrop-blur-xl p-4 rounded-2xl border-2 border-white/40 shadow-2xl"
                style={{
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(255, 255, 255, 0.2)'
                }}
              >
                <div className="flex items-center space-x-3">
                  <img 
                    src="https://api.dicebear.com/7.x/bottts/svg?seed=mairy&backgroundColor=c0aede"
                    alt=""
                    className="w-6 h-6 rounded-full animate-pulse border border-white/50"
                  />
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                  </div>
                  <span 
                    className="text-white font-medium text-sm"
                    style={{ 
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.9)'
                    }}
                  >
                    M.ai.ry responding...
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* RESTORED: Enhanced Text Input with Full Content */}
      <div className="fixed bottom-20 left-0 right-0 z-20 px-4">
        <div className="max-w-2xl mx-auto">
          
          {/* Enhanced Layout: Slider + Popup Text Box */}
          {isInputFocused && (
            <div className="mb-4 flex items-start space-x-4">
              
              {/* Background Control Slider - Extended vertically for usability */}
              <div className="flex flex-col items-center space-y-2 mt-8">
                <Eye className="w-4 h-4 text-white/60" />
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={backgroundOpacity}
                  onChange={(e) => setBackgroundOpacity(parseInt(e.target.value))}
                  className="accent-purple-400 opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
                  style={{
                    writingMode: 'bt-lr',
                    WebkitAppearance: 'slider-vertical',
                    transform: 'rotate(270deg)',
                    transformOrigin: 'center',
                    width: '120px',  // Extended length for better usability
                    height: '20px'   // Increased thickness for easier interaction
                  }}
                  title={`Background opacity: ${backgroundOpacity}%`}
                />
                <span className="text-white/60 text-xs">{backgroundOpacity}%</span>
              </div>
              
              {/* RESTORED: Expanded Content Popup - Clean text box */}
              <div className="flex-1 bg-black/80 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
                <div className="text-center space-y-2">
                  <h2 
                    className="text-xl font-bold text-white"
                    style={{ 
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 0 15px rgba(255, 255, 255, 0.6)'
                    }}
                  >
                    Secret History of Humankind
                  </h2>
                  
                  <p 
                    className="text-lg font-medium text-yellow-400"
                    style={{ 
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 0 10px rgba(255, 215, 0, 0.8)'
                    }}
                  >
                    Play to Remember....
                  </p>
                  
                  <div className="space-y-1 text-white/90 text-sm leading-relaxed">
                    <p style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                      share wisdom, captures, film, music, art that benefits you and all.
                    </p>
                    <p 
                      className="font-medium text-purple-300"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Shared reality creates reality.
                    </p>
                    <p 
                      className="text-white/60 text-xs italic mt-2"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      *Post only what is appropriate for Creative Commons, and all ages.
                    </p>
                    
                    {/* Reality Generation Status - Moved to Dialogue Box */}
                    <div className="mt-3 pt-2 border-t border-white/10">
                      <div 
                        className="text-green-400 text-xs font-medium animate-pulse flex items-center justify-center space-x-1"
                        style={{ 
                          fontFamily: '"Courier New", monospace',
                          textShadow: '0 0 4px rgba(34, 197, 94, 0.5)',
                          letterSpacing: '0.05em'
                        }}
                      >
                        <div className="w-1 h-1 bg-green-400 rounded-full"></div>
                        <span>REALITY GENERATION RUNNING</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div 
            className={`bg-gray-800/90 backdrop-blur-xl rounded-full transition-all duration-300 border border-white/20 ${
              isInputFocused ? 'px-5 py-3' : 'px-4 py-2.5'
            }`}
          >
            <div className="flex items-center space-x-3">
              <input
                type="text"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                onFocus={() => {
                  setIsInputFocused(true);
                  if (inputTimeoutRef.current) {
                    clearTimeout(inputTimeoutRef.current);
                  }
                }}
                onBlur={() => {
                  inputTimeoutRef.current = setTimeout(() => {
                    setIsInputFocused(false);
                  }, 3000);
                }}
                placeholder="What say you?"
                className="flex-1 bg-transparent text-white placeholder-white/60 focus:outline-none text-base"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif'
                }}
                disabled={isLoading}
              />
              
              <button
                onClick={sendMessage}
                disabled={!currentMessage.trim() || isLoading}
                className="text-cyan-400 hover:text-cyan-300 disabled:text-gray-500 transition-colors"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Audio Heart Toggle - Bottom Left of Main Display */}
      <div className="fixed z-50" style={{ bottom: '20px', left: '20px' }}>
        <button
          onClick={() => {
            const newAudioState = !audioEnabled;
            setAudioEnabled(newAudioState);
            
            if (window.planetaryFedHeartbeat) {
              window.planetaryFedHeartbeat.setEnabled(newAudioState);
              if (newAudioState) {
                window.planetaryFedHeartbeat.forceRestart();
              }
            }
          }}
          className={`text-sm hover:scale-125 transition-all duration-300 p-1 border-none outline-none opacity-90 hover:opacity-100 ${
            audioEnabled ? 'animate-pulse' : ''
          }`}
          style={{ 
            background: 'transparent',
            border: 'none',
            filter: audioEnabled 
              ? 'drop-shadow(0 0 6px rgba(255, 20, 147, 0.8))'
              : 'drop-shadow(0 0 4px rgba(0, 0, 0, 0.8))',
            animation: audioEnabled ? 'anatomical-heartbeat 1.2s ease-in-out infinite' : 'none'
          }}
          title={`${audioEnabled ? 'Mute all audio' : 'Enable all audio'}`}
        >
          <span style={{ 
            filter: audioEnabled ? 'brightness(1.2) saturate(1.4)' : 'brightness(0.6)',
            fontSize: '0.9em',
            display: 'inline-block'
          }}>
            🫀
          </span>
        </button>
        
        {audioEnabled && (
          <div className="absolute -top-0.5 -right-0.5">
            <div className="w-1.5 h-1.5 bg-red-400 rounded-full animate-ping opacity-75"></div>
          </div>
        )}
      </div>

      {/* RESTORED: Enhanced Mobile App Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-gray-900/95 backdrop-blur-xl border-t border-gray-700">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-around">
            
            {/* Upload */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center space-y-1 text-white/70 hover:text-white transition-colors"
            >
              <Upload className="w-5 h-5" />
              <span className="text-xs font-medium">Upload</span>
            </button>
            
            {/* Spiral Experience */}
            <button
              onClick={onNavigateToSpiralTrip}
              className="flex flex-col items-center space-y-1 text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              <div className="w-5 h-5 rounded-full bg-yellow-400 animate-pulse"></div>
              <span className="text-xs font-medium">Spiral</span>
            </button>
            
            {/* Sonic Experience - Grant's Dimensional Engine */}
            <button
              onClick={() => {
                if (typeof onNavigateToSonicExperience === 'function') {
                  onNavigateToSonicExperience();
                } else {
                  alert('Grant\'s Sonic Dimension - 7-D mathematical audio experience coming online...');
                }
              }}
              className="flex flex-col items-center space-y-1 text-purple-400 hover:text-purple-300 transition-colors"
            >
              <Volume2 className="w-5 h-5" />
              <span className="text-xs font-medium">Sonic</span>
            </button>
            
            {/* Projects - Navigate to new Project Management App */}
            <button
              onClick={() => {
                if (window.handleNavigateToMyProjects) {
                  window.handleNavigateToMyProjects();
                } else {
                  console.log('Navigate to Project Management App');
                }
              }}
              className="flex flex-col items-center space-y-1 text-blue-400 hover:text-blue-300 transition-colors"
            >
              <Briefcase className="w-5 h-5" />
              <span className="text-xs font-medium">Projects</span>
            </button>
            
            {/* RESTORED: TURING with Chime */}
            <button
              onClick={() => {
                // RESTORED: Play TURING chime
                try {
                  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                  const oscillator = audioContext.createOscillator();
                  const gainNode = audioContext.createGain();
                  
                  oscillator.connect(gainNode);
                  gainNode.connect(audioContext.destination);
                  
                  oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
                  oscillator.frequency.linearRampToValueAtTime(1200, audioContext.currentTime + 0.2);
                  
                  gainNode.gain.setValueAtTime(0, audioContext.currentTime);
                  gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
                  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
                  
                  oscillator.start(audioContext.currentTime);
                  oscillator.stop(audioContext.currentTime + 0.4);
                  
                  console.log('🤖 TURING: Vintage chime played');
                  
                  setTimeout(() => {
                    alert('TURING Test\n\nAdvanced human-AI collaboration gameplay coming soon...');
                  }, 500);
                  
                } catch (error) {
                  alert('TURING Test functionality coming soon...');
                }
              }}
              className="flex flex-col items-center space-y-1 text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              <Zap className="w-5 h-5" />
              <span className="text-xs font-medium">TURING</span>
            </button>
            
            {/* RESTORED: Nudge M.ai.ry */}
            <button
              onClick={() => {
                console.log('🧠 M.AI.RY NUDGE: Prompting real-time AI story generation');
                const nudgeMessage = "Please generate a story moment based on our conversation and the principles of belonging, regeneration, and authentic collaboration.";
                setCurrentMessage(nudgeMessage);
              }}
              className="flex flex-col items-center space-y-1 text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <Brain className="w-5 h-5" />
              <span className="text-xs font-medium">Nudge</span>
            </button>
            
            {/* NEW: Thread Game Summary */}
            <button
              onClick={() => {
                console.log('🎭 THREAD SUMMARY: Creating artistic story analysis...');
                const summaryMessage = {
                  id: 'story_summary_' + Date.now(),
                  type: 'story_summary',
                  content: `🎭 **Thread Story Summary**\n\nM.ai.ry weaves your conversation into an epic narrative of consciousness expansion and authentic belonging. Key themes: growth, connection, and reality creation through meaningful dialogue.`,
                  sender: 'M.ai.ry Storyteller',
                  timestamp: new Date(),
                  avatar: {
                    name: 'M.ai.ry',
                    image: 'https://api.dicebear.com/7.x/bottts/svg?seed=storyteller&backgroundColor=ffb347'
                  }
                };
                setMessages(prev => [...prev, summaryMessage]);
              }}
              className="flex flex-col items-center space-y-1 text-orange-400 hover:text-orange-300 transition-colors"
            >
              <Eye className="w-5 h-5" />
              <span className="text-xs font-medium">Story</span>
            </button>
            
            {/* Revolutionary: Consciousness Video Trailer Creation */}
            <button
              onClick={async () => {
                try {
                  console.log('🎬 CONSCIOUSNESS TRAILER: Creating personalized video montage...');
                  
                  // Step 1: Create M.ai.ry storyboard
                  const storyboard = await axios.post(`${API}/mairy/create-cinematic-montage`, {
                    player_id: player.id,
                    montage_type: 'seeker_agent_mission',
                    use_uploaded_content: true,
                    artistic_vision: 'project_actualization_epic',
                    cinematic_style: 'future_timeline_documentary'
                  });
                  
                  // Step 2: Generate actual video trailer
                  const trailer = await createConsciousnessTrailer(API, localStorage.getItem('planetary_fed_token'), sessionId);
                  
                  // Step 3: Add video message to chat
                  const trailerMessage = {
                    id: 'trailer_' + Date.now(),
                    type: 'consciousness_trailer',
                    content: `🎬 **Your Consciousness Expansion Trailer**\n\nM.ai.ry has woven your uploaded content and conversation themes into a personalized documentary of your seeker agent mission. This magical experience bridges your individual journey with the collective perception field.`,
                    sender: 'M.ai.ry Director',
                    timestamp: new Date(),
                    video_url: trailer.video_url,
                    magical_experience: true,
                    avatar: {
                      name: 'M.ai.ry',
                      image: 'https://api.dicebear.com/7.x/bottts/svg?seed=director&backgroundColor=8b5cf6'
                    }
                  };
                  
                  setMessages(prev => [...prev, trailerMessage]);
                  
                } catch (error) {
                  console.error('Consciousness trailer error:', error);
                  const demoMessage = {
                    id: 'trailer_demo_' + Date.now(),
                    type: 'system',
                    content: `🎬 **Consciousness Trailer System**\n\nRevolutionary feature: M.ai.ry creates personalized video montages from your uploaded content and conversation themes. Your individual journey becomes an epic documentary that bridges personal growth with collective perception influence.\n\n**Coming Online Soon:** 20-30 second consciousness expansion trailers with crossfades, spiral transitions, and ambient tracks.`,
                    sender: 'M.ai.ry Director',
                    timestamp: new Date(),
                    avatar: {
                      name: 'M.ai.ry',
                      image: 'https://api.dicebear.com/7.x/bottts/svg?seed=director&backgroundColor=8b5cf6'
                    }
                  };
                  
                  setMessages(prev => [...prev, demoMessage]);
                }
              }}
              className="flex flex-col items-center space-y-1 text-purple-400 hover:text-purple-300 transition-colors"
            >
              <div className="w-5 h-5 rounded-full bg-purple-400 animate-pulse"></div>
              <span className="text-xs font-medium">Trailer</span>
            </button>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.md"
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) {
            // Add upload confirmation message
            const uploadMessage = {
              id: 'upload_' + Date.now(),
              type: 'system',
              content: `📎 Uploaded: ${files[0].name} - Content being woven into reality...`,
              sender: 'System',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, uploadMessage]);
          }
          e.target.value = '';
        }}
        className="hidden"
      />
      
      <PersistentFooter />
    </div>
  );
};

export default MobileChatInterface;